import { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Search, Trash2, RefreshCw, Loader2, Mail, MessageSquare, Users, TrendingUp, Download, ShieldCheck, ShieldOff, KeyRound, X } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const STATUSES = ["new", "contacted", "converted", "rejected"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const token = localStorage.getItem("kiki_admin_token");
  const [tab, setTab] = useState("enquiries");
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [otpRequired, setOtpRequired] = useState(false);
  const [savingOtp, setSavingOtp] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState("");
  const [tracking, setTracking] = useState({ ga_measurement_id: "", gtm_id: "", google_ads_id: "", ads_conversion_label: "" });
  const [savingTracking, setSavingTracking] = useState(false);

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("kiki_admin_user") || "null"); } catch { return null; }
  }, []);

  const fetchAll = async () => {
    setRefreshing(true);
    try {
      const [s, e, n, m] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/enquiries", { params: { status_filter: filter, q } }),
        api.get("/admin/newsletter"),
        api.get("/admin/contact-messages"),
      ]);
      setStats(s.data);
      setEnquiries(e.data.items);
      setNewsletter(n.data.items);
      setMessages(m.data.items);
      try {
        const st = await api.get("/admin/settings");
        setOtpRequired(!!st.data.otp_required);
        setTracking({
          ga_measurement_id: st.data.ga_measurement_id || "",
          gtm_id: st.data.gtm_id || "",
          google_ads_id: st.data.google_ads_id || "",
          ads_conversion_label: st.data.ads_conversion_label || "",
        });
      } catch (_) {}
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
      } else {
        toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { if (token) fetchAll(); /* eslint-disable-next-line */ }, [filter, token]);

  if (!token) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    localStorage.removeItem("kiki_admin_token");
    localStorage.removeItem("kiki_admin_user");
    nav("/admin/login");
  };

  const search = () => fetchAll();

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    if (pwForm.next.length < 8) return setPwError("New password must be at least 8 characters.");
    if (pwForm.next !== pwForm.confirm) return setPwError("New password and confirmation do not match.");
    if (pwForm.next === pwForm.current) return setPwError("New password must be different from the current one.");
    setPwLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Password changed", { description: "Use your new password next time you log in." });
      setShowPw(false);
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPwError(formatApiError(err?.response?.data?.detail) || "Couldn't change password.");
    } finally {
      setPwLoading(false);
    }
  };

  const saveTracking = async (e) => {
    e.preventDefault();
    setSavingTracking(true);
    try {
      const { data } = await api.patch("/admin/settings", {
        ga_measurement_id: tracking.ga_measurement_id.trim(),
        gtm_id: tracking.gtm_id.trim(),
        google_ads_id: tracking.google_ads_id.trim(),
        ads_conversion_label: tracking.ads_conversion_label.trim(),
      });
      setTracking({
        ga_measurement_id: data.ga_measurement_id || "",
        gtm_id: data.gtm_id || "",
        google_ads_id: data.google_ads_id || "",
        ads_conversion_label: data.ads_conversion_label || "",
      });
      toast.success("Tracking settings saved", { description: "Scripts apply on the next page load." });
    } catch (err) {
      toast.error("Couldn't save tracking settings");
    } finally {
      setSavingTracking(false);
    }
  };

  const toggleOtp = async () => {
    const next = !otpRequired;
    setSavingOtp(true);
    try {
      const { data } = await api.patch("/admin/settings", { otp_required: next });
      setOtpRequired(!!data.otp_required);
      toast.success(next ? "OTP verification turned ON" : "OTP verification turned OFF", {
        description: next ? "Applicants must verify their phone (+91) via OTP." : "The enquiry form now submits directly, without OTP.",
      });
    } catch (e) {
      toast.error("Couldn't update OTP setting");
    } finally {
      setSavingOtp(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this enquiry permanently?")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      setEnquiries((list) => list.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/enquiries/export", {
        params: { status_filter: filter, q },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `kiki-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${enquiries.length} enquiries`);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={ADMIN.dashboard}>
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur-xl">
        <div className="max-w-[90rem] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/kiki-logo.png" alt="KIKI" className="h-8 w-auto select-none" draggable={false} />
            <div className="border-l border-border pl-3">
              <div className="label-overline text-muted-foreground text-[10px]">Admin Panel</div>
              <div className="font-sans font-bold text-[13px] leading-none mt-1">Session 2026 · Live</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchAll} aria-label="Refresh" className="h-10 w-10 grid place-items-center border border-border hover:border-foreground transition-colors">
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} />
            </button>
            <div className="hidden md:block label-overline text-muted-foreground">{user?.email}</div>
            <button data-testid="admin-change-password-btn" onClick={() => { setPwError(""); setShowPw(true); }} className="btn-ghost-pill" title="Change password">
              <KeyRound className="w-4 h-4" strokeWidth={1.8} /> <span className="hidden sm:inline">Password</span>
            </button>
            <button data-testid={ADMIN.logout} onClick={logout} className="btn-ghost-pill">
              <LogOut className="w-4 h-4" strokeWidth={1.8} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[90rem] mx-auto px-6 md:px-10 py-8 md:py-12">
        {/* Header row */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="label-overline text-muted-foreground">Session 2026 · Live Dashboard</div>
            <h1 className="font-sans font-black text-4xl md:text-5xl leading-[1] tracking-tight mt-2">Enquiries.</h1>
          </div>
          <div className="flex items-center gap-4 border border-border px-5 py-3" data-testid="admin-otp-setting">
            <div className="flex items-center gap-2.5">
              {otpRequired ? <ShieldCheck className="w-5 h-5 text-foreground" strokeWidth={1.6} /> : <ShieldOff className="w-5 h-5 text-muted-foreground" strokeWidth={1.6} />}
              <div>
                <div className="font-sans font-bold text-sm leading-none">Phone OTP verification</div>
                <div className="label-overline text-muted-foreground text-[10px] mt-1">
                  {otpRequired ? "ON · applicants verify via +91 OTP" : "OFF · form submits directly"}
                </div>
              </div>
            </div>
            <button
              data-testid="admin-otp-toggle"
              role="switch"
              aria-checked={otpRequired}
              disabled={savingOtp}
              onClick={toggleOtp}
              className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${otpRequired ? "bg-foreground" : "bg-border"}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-background shadow transition-transform ${otpRequired ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard testId={ADMIN.statsTotal} icon={Users} label="Total Enquiries" value={stats?.total ?? "—"} sub={`+${stats?.last_7_days ?? 0} last 7 days`} />
          <StatCard testId={ADMIN.statsNew} icon={Mail} label="New" value={stats?.new ?? "—"} sub={`${stats?.contacted ?? 0} contacted`} tone="yellow" />
          <StatCard testId={ADMIN.statsConverted} icon={TrendingUp} label="Converted" value={stats?.converted ?? "—"} sub={`${stats?.total ? Math.round(((stats?.converted || 0) / stats.total) * 100) : 0}% rate`} tone="dark" />
          <StatCard testId={ADMIN.statsSubs} icon={MessageSquare} label="Newsletter" value={stats?.newsletter_subs ?? "—"} sub={`${stats?.contact_messages ?? 0} messages`} />
        </div>

        {/* Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-10" data-testid="admin-analytics">
          <div className="lg:col-span-3 border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="label-overline text-muted-foreground">Enquiries · Last 14 days</div>
                <div className="font-sans font-bold text-lg mt-1">Daily volume</div>
              </div>
              <TrendingUp className="w-5 h-5 text-muted-foreground" strokeWidth={1.4} />
            </div>
            <div className="h-[220px] w-full" data-testid="admin-chart-daily">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats?.by_day || []} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="kikiFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FFCC00" stopOpacity={0.65} />
                      <stop offset="100%" stopColor="#FFCC00" stopOpacity={0.04} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={dayTick} tick={{ fontSize: 10, fontFamily: "monospace" }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                  <YAxis allowDecimals={false} width={28} tick={{ fontSize: 10, fontFamily: "monospace" }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTip />} cursor={{ stroke: "#FFCC00", strokeWidth: 1 }} />
                  <Area type="monotone" dataKey="count" stroke="#050505" strokeWidth={2} fill="url(#kikiFill)" dot={false} activeDot={{ r: 4, fill: "#FFCC00", stroke: "#050505" }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="lg:col-span-2 border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="label-overline text-muted-foreground">By programme</div>
                <div className="font-sans font-bold text-lg mt-1">Interest split</div>
              </div>
              <Users className="w-5 h-5 text-muted-foreground" strokeWidth={1.4} />
            </div>
            <div className="h-[220px] w-full" data-testid="admin-chart-programme">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.by_programme || []} layout="vertical" margin={{ top: 0, right: 12, left: 4, bottom: 0 }}>
                  <XAxis type="number" allowDecimals={false} hide />
                  <YAxis type="category" dataKey="programme" width={128} tick={{ fontSize: 10, fontFamily: "monospace" }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTip label="enquiries" />} cursor={{ fill: "hsl(var(--muted))" }} />
                  <Bar dataKey="count" fill="#FFCC00" radius={[0, 2, 2, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Tracking & marketing settings */}
        <form onSubmit={saveTracking} className="border border-border p-6 mb-10" data-testid="admin-tracking-settings">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label-overline text-muted-foreground">Marketing · Mandatory tracking</div>
              <div className="font-sans font-bold text-lg mt-1">Analytics, Tag Manager & Google Ads</div>
            </div>
            <TrendingUp className="w-5 h-5 text-muted-foreground" strokeWidth={1.4} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="label-overline text-muted-foreground">Google Analytics (GA4) ID</span>
              <input data-testid="admin-ga-id" value={tracking.ga_measurement_id} onChange={(e) => setTracking((s) => ({ ...s, ga_measurement_id: e.target.value }))} placeholder="G-XXXXXXXXXX" className="kiki-input mt-1.5" />
            </label>
            <label className="block">
              <span className="label-overline text-muted-foreground">Google Tag Manager ID</span>
              <input data-testid="admin-gtm-id" value={tracking.gtm_id} onChange={(e) => setTracking((s) => ({ ...s, gtm_id: e.target.value }))} placeholder="GTM-XXXXXXX" className="kiki-input mt-1.5" />
            </label>
            <label className="block">
              <span className="label-overline text-muted-foreground">Google Ads Conversion ID</span>
              <input data-testid="admin-ads-id" value={tracking.google_ads_id} onChange={(e) => setTracking((s) => ({ ...s, google_ads_id: e.target.value }))} placeholder="AW-XXXXXXXXX" className="kiki-input mt-1.5" />
            </label>
            <label className="block">
              <span className="label-overline text-muted-foreground">Google Ads Conversion Label</span>
              <input data-testid="admin-ads-label" value={tracking.ads_conversion_label} onChange={(e) => setTracking((s) => ({ ...s, ads_conversion_label: e.target.value }))} placeholder="AbC-D_efGhIjK" className="kiki-input mt-1.5" />
            </label>
          </div>
          <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
            These fire site-wide + a lead conversion on the Thank-you page. Leave blank to disable. Changes apply on next page load.
          </p>
          <button data-testid="admin-save-tracking" disabled={savingTracking} type="submit" className="btn-pill-yellow mt-5 px-8 py-3">
            {savingTracking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save tracking settings"}
          </button>
        </form>


        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-border mb-6">
          <TabBtn active={tab === "enquiries"} onClick={() => setTab("enquiries")} testId={ADMIN.tabEnquiries}>Enquiries · {enquiries.length}</TabBtn>
          <TabBtn active={tab === "newsletter"} onClick={() => setTab("newsletter")} testId={ADMIN.tabNewsletter}>Newsletter · {newsletter.length}</TabBtn>
          <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} testId={ADMIN.tabMessages}>Messages · {messages.length}</TabBtn>
        </div>

        {tab === "enquiries" && (
          <div>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-1 border border-border">
                {["all", ...STATUSES].map((s) => (
                  <button
                    key={s}
                    data-testid={s === "all" ? ADMIN.filterAll : s === "new" ? ADMIN.filterNew : s === "contacted" ? ADMIN.filterContacted : s === "converted" ? ADMIN.filterConverted : `admin-filter-${s}`}
                    onClick={() => setFilter(s)}
                    className={`label-overline px-4 py-2.5 border-r border-border last:border-r-0 transition-colors ${filter === s ? "bg-foreground text-background" : "hover:bg-muted"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 border border-border px-3 md:min-w-[260px]">
                <Search className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <input
                  data-testid={ADMIN.searchInput}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder="Search name / email / phone / city"
                  className="flex-1 py-2.5 bg-transparent focus:outline-none text-sm"
                />
                <button onClick={search} className="label-overline text-muted-foreground hover:text-foreground">Go</button>
              </div>
              <button
                data-testid="admin-export-csv"
                onClick={exportCsv}
                disabled={exporting || enquiries.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-border label-overline hover:bg-foreground hover:text-background transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" strokeWidth={1.6} />}
                Export CSV
              </button>
            </div>

            <div className="border border-border overflow-x-auto">
              <table data-testid={ADMIN.enquiriesTable} className="w-full text-sm min-w-[900px]">
                <thead className="border-b border-border">
                  <tr className="label-overline text-muted-foreground text-left">
                    <Th>Name</Th><Th>Contact</Th><Th>Programme</Th><Th>Qualification</Th><Th>City</Th><Th>When</Th><Th>Status</Th><Th> </Th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>
                  ) : enquiries.length === 0 ? (
                    <tr><td colSpan={8} className="p-16 text-center text-muted-foreground font-display italic text-2xl">No enquiries yet.</td></tr>
                  ) : (
                    enquiries.map((e) => (
                      <tr key={e.id} data-testid={ADMIN.enquiryRow} className="border-b border-border hover:bg-muted/40">
                        <Td>
                          <div className="font-medium">{e.name}</div>
                          <div className="label-overline text-muted-foreground text-[10px] mt-1">{e.id.slice(0, 8)}</div>
                        </Td>
                        <Td>
                          <a href={`mailto:${e.email}`} className="link-sweep block">{e.email}</a>
                          <a href={`tel:${e.phone}`} className="link-sweep block text-muted-foreground text-xs">{e.phone}</a>
                        </Td>
                        <Td>{e.programme}</Td>
                        <Td className="text-muted-foreground">{e.qualification}</Td>
                        <Td>{e.city}</Td>
                        <Td className="text-muted-foreground text-xs">{formatDate(e.created_at)}</Td>
                        <Td>
                          <select
                            data-testid={ADMIN.statusSelect}
                            value={e.status}
                            onChange={(ev) => updateStatus(e.id, ev.target.value)}
                            className={`px-2 py-1 border border-border bg-background text-xs font-mono uppercase tracking-wider ${statusTone(e.status)}`}
                          >
                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </Td>
                        <Td>
                          <button data-testid={ADMIN.deleteBtn} onClick={() => remove(e.id)} className="w-8 h-8 grid place-items-center border border-border hover:border-destructive hover:text-destructive transition-colors">
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </button>
                        </Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "newsletter" && (
          <div className="border border-border">
            <table className="w-full text-sm">
              <thead className="border-b border-border label-overline text-muted-foreground text-left">
                <tr><Th>Email</Th><Th>Subscribed</Th></tr>
              </thead>
              <tbody>
                {newsletter.length === 0 ? (
                  <tr><td colSpan={2} className="p-12 text-center text-muted-foreground font-display italic text-xl">No subscribers yet.</td></tr>
                ) : newsletter.map((s) => (
                  <tr key={s.id} className="border-b border-border">
                    <Td><a href={`mailto:${s.email}`} className="link-sweep">{s.email}</a></Td>
                    <Td className="text-muted-foreground text-xs">{formatDate(s.created_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-4">
            {messages.length === 0 ? (
              <div className="border border-border p-16 text-center text-muted-foreground font-display italic text-xl">No contact messages yet.</div>
            ) : messages.map((m) => (
              <div key={m.id} className="border border-border p-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-medium">{m.name}</div>
                    <a href={`mailto:${m.email}`} className="text-sm text-muted-foreground link-sweep">{m.email}</a>
                  </div>
                  <div className="label-overline text-muted-foreground">{formatDate(m.created_at)}</div>
                </div>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.message}</p>
              </div>
            ))}
          </div>
        )}
      </main>

      {showPw && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="change-password-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowPw(false)} />
          <form onSubmit={changePassword} className="relative w-full max-w-md bg-background border border-foreground shadow-[8px_8px_0_0_hsl(var(--foreground))] p-7">
            <button type="button" onClick={() => setShowPw(false)} className="absolute top-4 right-4 w-9 h-9 grid place-items-center rounded-full border border-border hover:border-foreground transition-colors" aria-label="Close">
              <X className="w-4 h-4" strokeWidth={1.8} />
            </button>
            <div className="flex items-center gap-2.5 mb-5">
              <KeyRound className="w-5 h-5" strokeWidth={1.6} />
              <h3 className="font-sans font-bold text-xl">Change password</h3>
            </div>
            <div className="space-y-4">
              <label className="block">
                <span className="label-overline text-muted-foreground">Current password</span>
                <input data-testid="cp-current" type="password" value={pwForm.current} onChange={(e) => setPwForm((s) => ({ ...s, current: e.target.value }))} className="kiki-input mt-1.5" autoComplete="current-password" />
              </label>
              <label className="block">
                <span className="label-overline text-muted-foreground">New password (min 8 chars)</span>
                <input data-testid="cp-new" type="password" value={pwForm.next} onChange={(e) => setPwForm((s) => ({ ...s, next: e.target.value }))} className="kiki-input mt-1.5" autoComplete="new-password" />
              </label>
              <label className="block">
                <span className="label-overline text-muted-foreground">Confirm new password</span>
                <input data-testid="cp-confirm" type="password" value={pwForm.confirm} onChange={(e) => setPwForm((s) => ({ ...s, confirm: e.target.value }))} className="kiki-input mt-1.5" autoComplete="new-password" />
              </label>
              {pwError && <div className="text-sm text-destructive font-mono" data-testid="cp-error">{pwError}</div>}
              <button data-testid="cp-submit" type="submit" disabled={pwLoading} className="btn-pill-yellow w-full py-3.5">
                {pwLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 font-mono text-[10px] tracking-[0.2em] uppercase">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
function TabBtn({ active, onClick, children, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className={`px-5 py-3 label-overline transition-colors border-b-2 ${active ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
      {children}
    </button>
  );
}
function StatCard({ icon: Icon, label, value, sub, tone, testId }) {
  const cls = tone === "dark" ? "bg-foreground text-background" : tone === "yellow" ? "bg-[#FFCC00] text-[#050505] border-transparent" : "bg-background";
  return (
    <div data-testid={testId} className={`border border-border p-6 ${cls}`}>
      <div className="flex items-center justify-between mb-4">
        <Icon className="w-5 h-5 opacity-70" strokeWidth={1.4} />
        <div className="label-overline opacity-60">{label}</div>
      </div>
      <div className="font-sans font-black text-5xl leading-[1] tracking-tight">{value}</div>
      <div className="label-overline opacity-60 mt-3">{sub}</div>
    </div>
  );
}
function statusTone(s) {
  if (s === "new") return "text-[#050505] bg-[#FFCC00]/40";
  if (s === "contacted") return "";
  if (s === "converted") return "bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-200";
  if (s === "rejected") return "bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200";
  return "";
}
function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso || "—"; }
}
function dayTick(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  } catch { return iso; }
}
function ChartTip({ active, payload, label, label: xLabel }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  const heading = p?.payload?.programme || (p?.payload?.date ? dayTick(p.payload.date) : "");
  return (
    <div className="border border-border bg-background px-3 py-2 shadow-lg">
      <div className="label-overline text-muted-foreground text-[10px]">{heading}</div>
      <div className="font-sans font-bold text-sm mt-0.5">{p.value} enquiries</div>
    </div>
  );
}
